import Button from '@rio-cloud/rio-uikit/Button';
import FeedbackInlineButtons from '@rio-cloud/rio-uikit/FeedbackInlineButtons';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { useState } from 'react';
import { StarRating } from 'react-flexible-star-rating';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCreateQuickFeedback } from '@/api/feedback';
import type { QuickFeedbackCreateType } from '@/api/schema';
import { trackFeedbackBannerClose, trackFeedbackBannerSubmit } from '@/features/dashboard/utils/analytics';

// Star configuration
const STARS_COUNT = 5;
const STAR_DIMENSION = 13;
const STAR_COLOR = '#fccc7c';

export const FEEDBACK_BANNER_DISMISSED_KEY = 'feedbackBannerDismissedUntil';
const DISMISS_DURATION_DAYS = 90;
const DISMISS_DURATION_MS = DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000;

const isBannerDismissed = (): boolean => {
    try {
        const storedValue = localStorage.getItem(FEEDBACK_BANNER_DISMISSED_KEY);

        if (!storedValue) {
            return false;
        }

        const dismissedUntil = Number(storedValue);

        return Number.isFinite(dismissedUntil) && Date.now() < dismissedUntil;
    } catch {
        return false;
    }
};

const persistDismissal = () => {
    try {
        localStorage.setItem(FEEDBACK_BANNER_DISMISSED_KEY, String(Date.now() + DISMISS_DURATION_MS));
    } catch {
        // Ignore storage errors (e.g. private mode / storage disabled).
    }
};

export const FeedbackBanner = () => {
    const intl = useIntl();
    const [isVisible, setIsVisible] = useState<boolean>(() => !isBannerDismissed());
    const { mutateAsync: createQuickFeedback } = useCreateQuickFeedback();

    const handleDismiss = () => {
        persistDismissal();
        trackFeedbackBannerClose();
        setIsVisible(false);

        Notification.success(intl.formatMessage({ id: 'feedbackBanner.dismissed.message' }), {
            title: intl.formatMessage({ id: 'feedbackBanner.dismissed.title' }),
        });
    };

    const handleRatingChange = async (rating: number) => {
        // react-flexible-star-rating reports 0 when a selected rating is toggled off.
        // Only a real rating (>= 1) counts as feedback submission.
        if (rating < 1) {
            return;
        }

        try {
            await createQuickFeedback({
                rating: String(rating) as QuickFeedbackCreateType['rating'],
                origin: 'OVERVIEW',
            });
        } catch {
            Notification.error(intl.formatMessage({ id: 'feedbackBanner.error.message' }), {
                title: intl.formatMessage({ id: 'feedbackBanner.error.title' }),
            });
            return;
        }

        persistDismissal();
        trackFeedbackBannerSubmit(rating);
        setIsVisible(false);

        Notification.success(intl.formatMessage({ id: 'feedbackBanner.submitted.message' }), {
            title: intl.formatMessage({ id: 'feedbackBanner.submitted.title' }),
        });
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className='panel panel-default margin-top-5 margin-bottom-0' data-testid='feedback-banner'>
            <div className='display-flex align-items-center gap-15 padding-y-15 padding-x-20'>
                <FeedbackInlineButtons
                    headline={
                        <span className='text-color-dark'>
                            <FormattedMessage id='feedbackBanner.title' />
                        </span>
                    }
                    buttons={[
                        <div key='feedback-banner-stars' className='display-inline-flex'>
                            <StarRating
                                starsLength={STARS_COUNT}
                                dimension={STAR_DIMENSION}
                                color={STAR_COLOR}
                                onRatingChange={handleRatingChange}
                            />
                        </div>,
                    ]}
                />
                <div className='display-flex align-items-center gap-5 margin-left-auto'>
                    <Button
                        bsStyle={Button.PRIMARY}
                        variant={Button.VARIANT_LINK}
                        className='text-color-primary padding-x-5'
                        onClick={handleDismiss}
                        data-testid='feedback-banner-no-thanks'
                    >
                        <FormattedMessage id='feedbackBanner.noThanks' />
                    </Button>
                    <Button
                        bsStyle={Button.MUTED}
                        iconOnly
                        onClick={handleDismiss}
                        aria-label={intl.formatMessage({ id: 'general.close' })}
                        data-testid='feedback-banner-close'
                    >
                        <span className='uikit-icon icon-remove' />
                    </Button>
                </div>
            </div>
        </div>
    );
};
